import { ChevronsUpDown, Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { listOrganisations } from '../organisations/api'
import type { CrmOrganisationListItem } from '../organisations/types'

export type OrganisationChoice =
  | { kind: 'existing'; id: string; name: string }
  | { kind: 'new'; name: string }

/** How long typing has to pause before the search goes out. */
const SEARCH_DELAY_MS = 250

/**
 * The list of a popover that opens from inside a modal ("Nieuw contact"): the modal's scroll
 * lock cancels wheel and touch events from outside its own DOM, and the popover is portaled
 * to <body>. Stopping them here keeps the list scrollable (as in CategorySelect).
 */
const keepScrolling = (event: { stopPropagation: () => void }) => event.stopPropagation()

/** The answer to the search for `text`; `items` is null when that search failed. */
interface Answer {
  text: string
  items: CrmOrganisationListItem[] | null
}

/**
 * Pick an existing organisation or name a new one ("Nieuwe organisatie 'X' aanmaken",
 * design §4.2). The caller creates or links it.
 *
 * Only the answer to the text in the box is listed: an older one would offer organisations
 * that do not match it, and Enter, typed faster than the search, would take the first of them.
 */
export function OrganisationCombobox({
  value,
  onChange,
  label = 'Organisatie',
  autoFocus,
  excludeId,
  allowNew = true,
}: {
  value: OrganisationChoice | null
  onChange: (choice: OrganisationChoice | null) => void
  label?: string
  autoFocus?: boolean
  /** An organisation that is not offered (the merge picker leaves out the page's own). */
  excludeId?: string
  /** False when only existing organisations may be chosen. */
  allowNew?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [answer, setAnswer] = useState<Answer | null>(null)
  const timer = useRef<number | undefined>(undefined)
  /** Numbers the searches: an answer that is not for the newest one is dropped. */
  const lookup = useRef(0)
  const trimmed = query.trim()
  const current = answer !== null && answer.text === trimmed ? answer : null
  const searching = current === null
  const failed = current !== null && current.items === null
  const options = (current?.items ?? []).filter((option) => option.id !== excludeId)
  const exact = options.some((option) => option.name.toLowerCase() === trimmed.toLowerCase())

  useEffect(() => () => window.clearTimeout(timer.current), [])

  function search(text: string, delay: number) {
    window.clearTimeout(timer.current)
    lookup.current += 1
    const mine = lookup.current
    function run() {
      listOrganisations({ q: text || undefined, page_size: 8 })
        .then((result) => {
          if (mine === lookup.current) setAnswer({ text, items: result.items })
        })
        .catch(() => {
          if (mine === lookup.current) setAnswer({ text, items: null })
        })
    }
    if (delay > 0) timer.current = window.setTimeout(run, delay)
    else run()
  }

  function typeText(next: string) {
    setQuery(next)
    search(next.trim(), SEARCH_DELAY_MS)
  }

  function changeOpen(next: boolean) {
    setOpen(next)
    // Every opening asks again: the list may have changed, or the last search failed.
    if (next) search(trimmed, 0)
  }

  function choose(choice: OrganisationChoice | null) {
    onChange(choice)
    setOpen(false)
    setQuery('')
  }

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          autoFocus={autoFocus}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-lg border border-border bg-background px-3 text-left text-sm',
            !value && 'text-muted-foreground',
          )}
        >
          <span className="truncate">
            {value
              ? value.kind === 'new'
                ? `${value.name} (nieuw)`
                : value.name
              : 'Geen organisatie'}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={typeText}
            placeholder="Zoek of typ een nieuwe organisatie…"
          />
          <CommandList onWheel={keepScrolling} onTouchMove={keepScrolling}>
            {searching ? (
              <p role="status" className="py-4 text-center text-sm text-muted-foreground">
                Zoeken…
              </p>
            ) : failed ? (
              <p role="alert" className="py-4 text-center text-sm text-destructive">
                Zoeken mislukt. Probeer het opnieuw.
              </p>
            ) : (
              <CommandEmpty>Geen organisaties gevonden.</CommandEmpty>
            )}
            <CommandGroup>
              {value && !trimmed ? (
                <CommandItem value="__geen__" onSelect={() => choose(null)}>
                  Geen organisatie
                </CommandItem>
              ) : null}
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  onSelect={() => choose({ kind: 'existing', id: option.id, name: option.name })}
                >
                  <span className="break-words">{option.name}</span>
                  {option.city ? (
                    <span className="shrink-0 text-muted-foreground">· {option.city}</span>
                  ) : null}
                </CommandItem>
              ))}
              {/* Not while the existing names are not known (searching, or the search failed). */}
              {allowNew && trimmed && current !== null && !failed && !exact ? (
                <CommandItem value="__nieuw__" onSelect={() => choose({ kind: 'new', name: trimmed })}>
                  <Plus className="size-4 shrink-0" aria-hidden />
                  Nieuwe organisatie &lsquo;{trimmed}&rsquo; aanmaken
                </CommandItem>
              ) : null}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
